import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { LoadingState, ErrorState, ApiModeBanner } from "../components/AdminUI";
import { useToast } from "../../../components/base/Toast";
import { isSupabaseConfigured } from "../../../lib/supabaseClient";
import {
  fetchUnbatchedOrders,
  fetchCityProgressView,
  fetchDispatchBatches,
  createDispatchBatch,
  fetchRelayThreadsForOrders,
  buildCityProgress,
  P_THRESHOLD,
  type DispatchBatch,
  type CityProgress,
  type RelayThreadStub,
} from "../../../api/dispatchSupabaseApi";
import type { Order } from "../../../store/types";
import CityProgressCards from "./components/CityProgressCards";
import ReadyToDispatch from "./components/ReadyToDispatch";
import UnbatchedOrdersTable from "./components/UnbatchedOrdersTable";

const IS_SUPABASE = isSupabaseConfigured();

interface PageState {
  unbatchedOrders: Order[];
  batches: DispatchBatch[];
  cityProgress: CityProgress[];
  loading: boolean;
  error: string | null;
}

interface DispatchRelayResult {
  city: string;
  batchId: string;
  orderIds: string[];
  threads: RelayThreadStub[];
}

const EMPTY_STATE: PageState = {
  unbatchedOrders: [],
  batches: [],
  cityProgress: [],
  loading: false,
  error: null,
};

// ─── Relay confirmation banner ────────────────────────────────────────────────

function RelayConfirmBanner({ result, onDismiss, onGoToRelay }: {
  result: DispatchRelayResult;
  onDismiss: () => void;
  onGoToRelay: (threadId?: string) => void;
}) {
  const hasThreads = result.threads.length > 0;
  const customerThreads = result.threads.filter((t) => t.participant_type === "customer");
  const photographerThreads = result.threads.filter((t) => t.participant_type === "photographer");

  return (
    <div className="mb-6 bg-emerald-50 border border-emerald-200 rounded-lg px-5 py-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-emerald-100 text-emerald-600 shrink-0 mt-0.5">
            <i className="ri-checkbox-circle-line text-lg" />
          </div>
          <div>
            <p className="text-sm font-medium text-emerald-800 mb-1">
              {hasThreads ? "排班通知已自動建立" : "排班批次已建立"}
            </p>
            <p className="text-xs text-emerald-700/70 font-light mb-2">
              {hasThreads
                ? `${result.city} · 已為 ${result.orderIds.length} 張訂單自動開啟 ${result.threads.length} 條訊息串`
                : `${result.city} · 批次已建立，後端排班通知正在處理中`}
            </p>
            {hasThreads && (
              <div className="flex flex-wrap gap-2 mb-3">
                {customerThreads.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-light">
                    <i className="ri-user-line mr-1 text-[9px]" />客戶通知 × {customerThreads.length}
                  </span>
                )}
                {photographerThreads.length > 0 && (
                  <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-light">
                    <i className="ri-camera-line mr-1 text-[9px]" />攝影師通知 × {photographerThreads.length}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={() => onGoToRelay(result.threads[0]?.id)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-light hover:bg-emerald-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <div className="w-3.5 h-3.5 flex items-center justify-center">
                  <i className="ri-message-2-line text-xs" />
                </div>
                前往 Relay 訊息中心
              </button>
              {!hasThreads && (
                <button
                  onClick={() => void fetchRelayThreadsForOrders(result.orderIds).then(threads => {
                    if (threads.length > 0) onGoToRelay(threads[0]?.id);
                    else onGoToRelay();
                  })}
                  className="text-xs text-emerald-600 underline cursor-pointer whitespace-nowrap font-light"
                >
                  重新檢查
                </button>
              )}
            </div>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className="w-6 h-6 flex items-center justify-center text-emerald-400 hover:text-emerald-600 transition-colors cursor-pointer shrink-0"
        >
          <i className="ri-close-line text-sm" />
        </button>
      </div>
    </div>
  );
}

// ─── Summary stat card ────────────────────────────────────────────────────────

function StatCard({ label, value, sub, icon }: {
  label: string; value: string | number; sub?: string; icon: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-[#D9D9D9]/60 px-5 py-4 flex items-center gap-4">
      <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#F7F6F3] text-[#C8B8A6] shrink-0">
        <i className={`${icon} text-lg`} />
      </div>
      <div>
        <p className="text-[11px] text-[#2C2C2C]/40 uppercase tracking-wider font-light">{label}</p>
        <p className="text-xl font-light text-[#2C2C2C]">{value}</p>
        {sub && <p className="text-[11px] text-[#2C2C2C]/30">{sub}</p>}
      </div>
    </div>
  );
}

// ─── Section heading ──────────────────────────────────────────────────────────

function SectionHeading({ title, count }: { title: string; count?: number }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h2 className="text-sm font-light text-[#2C2C2C] uppercase tracking-wider">{title}</h2>
      {count !== undefined && (
        <span className="px-2 py-0.5 bg-[#F7F6F3] rounded-full text-xs text-[#2C2C2C]/40">{count}</span>
      )}
      <div className="flex-1 h-px bg-[#D9D9D9]/60" />
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminDispatchPage() {
  const toast = useToast();
  const navigate = useNavigate();
  const [ps, setPs] = useState<PageState>({ ...EMPTY_STATE, loading: IS_SUPABASE });
  const [relayResult, setRelayResult] = useState<DispatchRelayResult | null>(null);

  const loadData = useCallback(async () => {
    if (!IS_SUPABASE) return;
    setPs((p) => ({ ...p, loading: true, error: null }));
    try {
      const [orders, progressRows, batches] = await Promise.all([
        fetchUnbatchedOrders(),
        fetchCityProgressView(),
        fetchDispatchBatches(),
      ]);
      const cityProgress = buildCityProgress(progressRows, orders);
      setPs({ unbatchedOrders: orders, batches, cityProgress, loading: false, error: null });
    } catch (e) {
      setPs((p) => ({ ...p, loading: false, error: (e as Error).message }));
    }
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  const handleCreateBatch = useCallback(async (city: string, orders: Order[]) => {
    try {
      const batch = await createDispatchBatch(city, orders);
      toast.success(`Batch created for ${city} — ${batch.total_p_units} P, ${batch.order_count} orders`);

      // Query for auto-created relay threads (backend trigger fires after batch/order assignment)
      const orderIds = orders.map((o) => o.id);
      await new Promise((r) => setTimeout(r, 600));
      const threads = await fetchRelayThreadsForOrders(orderIds);

      setRelayResult({ city, batchId: batch.id, orderIds, threads });
      await loadData();
    } catch (e) {
      toast.error(`Failed to create batch: ${(e as Error).message}`);
      throw e;
    }
  }, [loadData, toast]);

  const handleGoToRelay = useCallback((threadId?: string) => {
    const path = "/admin/relay" + (threadId ? `?thread=${threadId}` : "");
    navigate(path);
  }, [navigate]);

  // ── Derived stats ─────────────────────────────────────────────────────────
  const readyCities = ps.cityProgress.filter((cp) => cp.is_ready);
  const totalUnbatchedP = ps.cityProgress.reduce((s, c) => s + c.total_p, 0);
  const totalUnbatchedOrders = ps.unbatchedOrders.length;
  const totalBatchesCreated = ps.batches.length;

  return (
    <AdminLayout
      title="Dispatch & P-System"
      subtitle={`P threshold per city: ${P_THRESHOLD} P · Accumulates from unbatched orders`}
      action={
        <button
          onClick={() => void loadData()}
          className="flex items-center gap-2 px-4 py-2 border border-[#D9D9D9] text-[#2C2C2C]/60 text-sm rounded-md hover:border-[#2C2C2C]/30 hover:text-[#2C2C2C] transition-colors cursor-pointer whitespace-nowrap"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-refresh-line text-base" />
          </div>
          Refresh
        </button>
      }
    >
      {/* Supabase mode banner */}
      <ApiModeBanner isApiMode={IS_SUPABASE} />

      {/* Relay confirmation banner — shown after batch creation */}
      {relayResult && (
        <RelayConfirmBanner
          result={relayResult}
          onDismiss={() => setRelayResult(null)}
          onGoToRelay={handleGoToRelay}
        />
      )}

      {/* Summary stats */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <StatCard label="Unbatched Orders" value={totalUnbatchedOrders} sub="awaiting dispatch" icon="ri-file-list-line" />
        <StatCard label="Total Unbatched P" value={`${totalUnbatchedP} P`} sub="across all cities" icon="ri-bar-chart-box-line" />
        <StatCard label="Cities Ready" value={readyCities.length} sub={`≥ ${P_THRESHOLD} P`} icon="ri-map-pin-line" />
        <StatCard label="Batches Created" value={totalBatchesCreated} sub="all time" icon="ri-stack-line" />
      </div>

      {ps.loading ? (
        <LoadingState />
      ) : ps.error ? (
        <ErrorState message={ps.error} onRetry={loadData} />
      ) : (
        <>
          {/* Section 1 — City Progress */}
          <div className="mb-8">
            <SectionHeading title="City P Progress" count={ps.cityProgress.length} />
            <CityProgressCards cityProgress={ps.cityProgress} />
          </div>

          {/* Section 2 — Ready to Dispatch */}
          <div className="mb-8">
            <SectionHeading title="Ready to Dispatch" count={readyCities.length} />
            <ReadyToDispatch readyCities={readyCities} onCreateBatch={handleCreateBatch} />
          </div>

          {/* Section 3 — Unbatched Orders */}
          <div className="mb-8">
            <SectionHeading title="Orders Without Batch" count={totalUnbatchedOrders} />
            <UnbatchedOrdersTable orders={ps.unbatchedOrders} />
          </div>
        </>
      )}
    </AdminLayout>
  );
}
